import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';

// Field aliases: form/JS camelCase key → actual DB snake_case column
const JS_TO_DB = {
  clientName:   'client',
  contactName:  'contact',
  contactEmail: 'email',
  contactPhone: 'phone',
  jobType:      'type',
  deliveryLinks:'delivery_link',
  discount:     'discount_amt',
  propertyType: 'property_type',
};
const DB_TO_JS = Object.fromEntries(
  Object.entries(JS_TO_DB).map(([js, db]) => [db, js])
);

// Mapper: DB row (snake_case) → JS object (camelCase)
function mapFromDb(row) {
  const base = snakeToCamel(row);
  // Apply reverse aliases (DB column name → JS field name)
  for (const [dbSnake, jsKey] of Object.entries(DB_TO_JS)) {
    const camelOfSnake = dbSnake.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    if (camelOfSnake in base && !(jsKey in base)) {
      base[jsKey] = base[camelOfSnake];
      delete base[camelOfSnake];
    }
  }
  return base;
}

// Mapper: JS object → DB row for writes
function mapToDb(obj) {
  // Remap aliased fields before generic camelToSnake
  const remapped = { ...obj };
  for (const [jsKey, dbCol] of Object.entries(JS_TO_DB)) {
    if (jsKey in remapped) {
      remapped[dbCol] = remapped[jsKey];
      delete remapped[jsKey];
    }
  }
  return camelToSnake(remapped);
}

export function useJobs({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.jobs.all,
    queryFn: async () => {
      const data = await api.get('/jobs');
      return data.map(mapFromDb);
    },
    enabled,
  });
}

export function useJob(id) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(id),
    queryFn: async () => {
      const data = await api.get(`/jobs/${id}`);
      return mapFromDb(data);
    },
    enabled: !!id,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newJob) => {
      const data = await api.post('/jobs', mapToDb(newJob));
      return mapFromDb(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
      // Audit logging for jobs lives server-side now (server/routes/jobs.js), so
      // sweepers, Xero cascades and scripts are covered too — and the row carries
      // the field-level diff, which this could never provide.
    },
  });
}

export function useUpdateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (job) => {
      const { id, ...updates } = job;
      const data = await api.put(`/jobs/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data, job) => {
      qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
      qc.invalidateQueries({ queryKey: queryKeys.jobs.detail(data.id) });
      // A confirmed client change pins the job's issued invoices to the old
      // client server-side, so the cached invoices are stale.
      if (job.allowIdentityChange) qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Audit row is written server-side with the before/after diff.
    },
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId) => {
      // Server snapshots the job and everything that cascades with it into
      // Trash, then deletes. A refusal (403 not in the split / wrong status,
      // 409 Xero or bulk invoice) throws ApiError carrying the server's sentence.
      return api.del(`/jobs/${jobId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      // Cascaded payout lines, SET NULL quote/crew-earnings links, the audit row.
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({ queryKey: ['job-earnings-ledger'] });
      qc.invalidateQueries({ queryKey: queryKeys.crewEarnings.all });
      qc.invalidateQueries({ queryKey: queryKeys.actionLog.all });
      qc.invalidateQueries({ queryKey: queryKeys.trash.all });
    },
  });
}
